import PriceArrowDown from '@assets/icons/price-arrow-down.svg';
import PriceArrowUp from '@assets/icons/price-arrow-up.svg';
import React from 'react';
import { Badge } from '@usepepper/ui/atoms/badge';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import { ItemDetailCard } from '@/experiences/sales-portal/domain/items/components/item-detail-card';
import {
  ItemDetailDisplayConfig,
  ItemDetailSectionBadge,
} from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { ItemDetailSectionWrapper } from '@/experiences/sales-portal/domain/items/components/item-detail-section-wrapper';
import { PriceEditDisplayInfoTrend } from '@/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const EMPTY_VALUE = '--';
const TREND_ICON_SIZE = 12;

export interface ItemDetailSectionRow {
  currency?: string | null;
  detail?: React.ReactNode;
  key: string;
  label: string;
  trend?: PriceEditDisplayInfoTrend | null;
  value: string | null;
}

export type ItemDetailSectionContent =
  | { badges: ItemDetailSectionBadge[]; kind: 'badges' }
  | { kind: 'rows'; rows: ItemDetailSectionRow[] };

export interface ItemDetailSectionProps {
  content: ItemDetailSectionContent;
  hidden?: boolean;
  hideWhenEmpty?: boolean;
  title: string;
}

export interface ItemDetailSectionConfig extends ItemDetailSectionProps {
  key: string;
}

export const displayConfigToRow = (
  config: ItemDetailDisplayConfig,
): ItemDetailSectionRow => ({
  currency: config.currency,
  key: config.key,
  label: config.title,
  trend: config.trend,
  value: config.value,
});

/**
 * Single bordered card with a heading and either a row of badges or a stack
 * of label-left / value-right rows. Shared by every item-detail tab body
 * (General: Status, Pricing info, Purchase information, Custom info;
 * Details: Product specs, Shipping info).
 *
 * Sections with specialized layouts (Purchase history cards, Description
 * card, Related variant packs list) render their own wrapper directly —
 * they don't route through this component.
 *
 * @param props - {@link ItemDetailSectionConfig}.
 * @returns The rendered section, or `null` when hidden / empty.
 */
export const ItemDetailSection: React.FC<ItemDetailSectionProps> = ({
  content,
  hidden,
  hideWhenEmpty = true,
  title,
}) => {
  const [styles] = useStyles();

  if (hidden) {
    return null;
  }

  const isEmpty =
    content.kind === 'badges'
      ? content.badges.length === 0
      : content.rows.length === 0;

  if (isEmpty && hideWhenEmpty) {
    return null;
  }

  return (
    <ItemDetailSectionWrapper title={title}>
      <ItemDetailCard>
        {content.kind === 'badges' ? (
          <View style={styles.badgesRow}>
            {content.badges.map((badge) => (
              <Badge
                key={badge.key}
                label={badge.label}
                leftElement={badge.leftElement}
                size={badge.size}
                variant={badge.variant}
              />
            ))}
          </View>
        ) : (
          content.rows.map((row) => <SectionRow key={row.key} row={row} />)
        )}
      </ItemDetailCard>
    </ItemDetailSectionWrapper>
  );
};

const SectionRow: React.FC<{
  row: ItemDetailSectionRow;
}> = ({ row }) => {
  const [styles] = useStyles();
  const theme = useTheme();
  return (
    <View style={[styles.row, row.detail ? styles.rowWithDetail : undefined]}>
      <Typography
        color={theme.sk.color.text.primary}
        numberOfLines={1}
        variant="paragraph-md"
      >
        {row.label}
      </Typography>
      <View
        style={[
          styles.valueColumn,
          row.detail ? styles.valueColumnWithDetail : undefined,
        ]}
      >
        <View style={styles.valueGroup}>
          {row.trend === PriceEditDisplayInfoTrend.Up ? (
            <PriceArrowUp height={TREND_ICON_SIZE} width={TREND_ICON_SIZE} />
          ) : null}
          {row.trend === PriceEditDisplayInfoTrend.Down ? (
            <PriceArrowDown height={TREND_ICON_SIZE} width={TREND_ICON_SIZE} />
          ) : null}
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={1}
            variant="paragraph-md-600"
          >
            {formatRowValue(row)}
          </Typography>
        </View>
        {row.detail}
      </View>
    </View>
  );
};

const formatRowValue = (row: ItemDetailSectionRow): string => {
  if (row.value === null || row.value === '') {
    return EMPTY_VALUE;
  }
  if (row.currency) {
    const numeric = Number(row.value);
    if (Number.isFinite(numeric)) {
      return formatCurrency(numeric * MICRO_CONVERSION, row.currency);
    }
  }
  return row.value;
};

const useStyles = createStyles((theme) => ({
  badgesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceXsmall,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  rowWithDetail: {
    alignItems: 'flex-start',
  },
  valueColumn: {
    alignItems: 'flex-end',
  },
  valueColumnWithDetail: {
    flexShrink: 1,
  },
  valueGroup: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
